import React, { useState, useEffect, useRef } from 'react';
import {
    Badge,
    IconButton,
    Menu,
    MenuItem,
    Typography,
    Box,
    Divider,
    ListItemIcon,
    ListItemText,
    Button,
    CircularProgress,
    useTheme
} from '@mui/material';
import SecondaryButton from './SecondaryButton';
import {
    Notifications as NotificationsIcon,
    CheckCircle as CheckCircleIcon,
    Done as DoneIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    Settings as SettingsIcon,
    Assignment as AssignmentIcon,
    Person as PersonIcon,
    CreditCard as CreditCardIcon,
    Alarm as AlarmIcon
} from '@mui/icons-material';
import { format, isToday, isYesterday } from 'date-fns';
import { pt } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import notificacaoService, { Notificacao } from '../services/notificacaoService';

const NotificacaoIcone: React.FC = () => {
    const [notificacoes, setNotificacoes] = useState<Notificacao[]>([]);
    const [contador, setContador] = useState<number>(0);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [carregando, setCarregando] = useState<boolean>(false);
    const theme = useTheme();
    const navigate = useNavigate();
    const menuRef = useRef<HTMLDivElement>(null);
    const intervaloRef = useRef<NodeJS.Timeout | null>(null);

    // Carregar notificações não lidas
    const carregarNotificacoes = async () => {
        // try {
        //     setCarregando(true);
        //     const naoLidas = await notificacaoService.buscarNotificacoesNaoLidas();
        //     setNotificacoes(naoLidas);
        //     setContador(naoLidas.length);
        // } catch (error) {
        //     console.error('Erro ao carregar notificações:', error);
        // } finally {
        //     setCarregando(false);
        // }
    };

    // Atualizar contador de notificações não lidas
    const atualizarContador = async () => {
        // try {
        //     const count = await notificacaoService.contarNotificacoesNaoLidas();
        //     setContador(count);
        // } catch (error) {
        //     console.error('Erro ao atualizar contador:', error);
        // }
    };

    // Iniciar timer para verificar novas notificações
    useEffect(() => {
        carregarNotificacoes();

        // Verificar notificações a cada 60 segundos
        intervaloRef.current = setInterval(() => {
            atualizarContador();
        }, 60000);

        return () => {
            if (intervaloRef.current) {
                clearInterval(intervaloRef.current);
            }
        };
    }, []);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        carregarNotificacoes();
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleNotificacaoClick = async (notificacao: Notificacao) => {
        try {
            // Marcar como lida
            await notificacaoService.marcarComoLida(notificacao.guid);

            // Atualizar a lista local
            setNotificacoes(prev =>
                prev.map(n => n.guid === notificacao.guid ? { ...n, lida: true } : n)
            );

            // Atualizar contador
            atualizarContador();

            // Fechar menu
            handleClose();

            // Navegar para o link da ação, se existir
            if (notificacao.linkAcao) {
                navigate(notificacao.linkAcao);
            }
        } catch (error) {
            console.error('Erro ao processar notificação:', error);
        }
    };

    const handleMarcarTodasComoLidas = async () => {
        try {
            await notificacaoService.marcarTodasComoLidas();
            setNotificacoes([]);
            setContador(0);
            handleClose();
        } catch (error) {
            console.error('Erro ao marcar todas como lidas:', error);
        }
    };

    const handleVerTodasNotificacoes = () => {
        handleClose();
        navigate('/tw/notificacoes');
    };

    // Formatação de data relativa
    const formatarData = (dataString: string) => {
        const data = new Date(dataString);

        if (isToday(data)) {
            return `Hoje às ${format(data, 'HH:mm')}`;
        } else if (isYesterday(data)) {
            return `Ontem às ${format(data, 'HH:mm')}`;
        } else {
            return format(data, "dd 'de' MMMM 'às' HH:mm", { locale: pt });
        }
    };

    // Obter ícone para o tipo de notificação
    const getIcone = (tipo: string) => {
        switch (tipo) {
            case 'SUCCESS':
                return <CheckCircleIcon color="success" />;
            case 'WARNING':
                return <WarningIcon color="warning" />;
            case 'ERROR':
                return <ErrorIcon color="error" />;
            case 'SYSTEM':
                return <SettingsIcon color="secondary" />;
            case 'SUBSCRIPTION':
                return <CreditCardIcon color="primary" />;
            case 'ACCOUNT':
                return <PersonIcon color="primary" />;
            case 'TASK':
                return <AssignmentIcon color="primary" />;
            case 'REMINDER':
                return <AlarmIcon color="primary" />;
            case 'INFO':
            default:
                return <InfoIcon color="primary" />;
        }
    };

    return (
        <>
            <IconButton
                size="large"
                color="inherit"
                onClick={handleClick}
                aria-label="mostrar notificações"
            >
                <Badge badgeContent={contador} color="error">
                    <NotificationsIcon />
                </Badge>
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                    elevation: 3,
                    sx: {
                        maxHeight: 'calc(100% - 64px)',
                        width: {
                            xs: 'calc(100% - 32px)',
                            sm: 400
                        },
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
                        mt: 1.5,
                        '& .MuiMenuItem-root': {
                            px: 2,
                            py: 1.5
                        }
                    }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                ref={menuRef}
            >
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="h6" component="div">
                        Notificações
                    </Typography>
                    {contador > 0 && (
                        <Typography variant="body2" color="text.secondary">
                            Você tem {contador} {contador === 1 ? 'notificação não lida' : 'notificações não lidas'}
                        </Typography>
                    )}
                </Box>

                <Divider />

                {carregando ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : notificacoes.length === 0 ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                        <InfoIcon color="disabled" sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                        <Typography variant="body1" color="text.secondary">
                            Você não tem notificações não lidas
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {notificacoes.map((notificacao) => (
                            <MenuItem
                                key={notificacao.guid}
                                onClick={() => handleNotificacaoClick(notificacao)}
                                sx={{
                                    backgroundColor: notificacao.lida ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                                    borderLeft: notificacao.lida
                                        ? 'none'
                                        : `4px solid ${notificacaoService.getCorNotificacao(notificacao.tipoNotificacao)}`,
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.08)'
                                    }
                                }}
                            >
                                <ListItemIcon>
                                    {getIcone(notificacao.tipoNotificacao)}
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography variant="subtitle2" sx={{ fontWeight: notificacao.lida ? 'normal' : 'bold' }}>
                                            {notificacao.titulo}
                                        </Typography>
                                    }
                                    secondary={
                                        <>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                {notificacao.mensagem}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatarData(notificacao.dataEnvio)}
                                            </Typography>
                                            {notificacao.linkAcao && notificacao.textoAcao && (
                                                <Box sx={{ mt: 1 }}>
                                                    <SecondaryButton
                                                        variant="contained"
                                                        size="small"
                                                        onClick={(e: React.MouseEvent) => {
                                                            e.stopPropagation();
                                                            handleNotificacaoClick(notificacao);
                                                        }}
                                                    >
                                                        {notificacao.textoAcao}
                                                    </SecondaryButton>
                                                </Box>
                                            )}
                                        </>
                                    }
                                    primaryTypographyProps={{ variant: 'subtitle2' }}
                                    sx={{ margin: 0 }}
                                />
                            </MenuItem>
                        ))}
                    </>
                )}

                <Divider />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1.5 }}>
                    {contador > 0 && (
                        <Button
                            startIcon={<DoneIcon />}
                            onClick={handleMarcarTodasComoLidas}
                            size="small"
                        >
                            Marcar todas como lidas
                        </Button>
                    )}
                    <Button
                        onClick={handleVerTodasNotificacoes}
                        size="small"
                        sx={{ ml: 'auto' }}
                    >
                        Ver todas
                    </Button>
                </Box>
            </Menu>
        </>
    );
};

export default NotificacaoIcone;
